import { useMemo } from "react";
import {
  JALALI_MONTHS_FA,
  jalaliMonthLength,
  jalaliPartsToIso,
  isoToJalaliParts,
  nowJalaliParts,
  toFaDigits,
  type JalaliDateTimeParts,
} from "./jalali";

function range(from: number, to: number): number[] {
  const out: number[] = [];
  for (let i = from; i <= to; i++) out.push(i);
  return out;
}

export function JalaliDateTimeField(props: {
  readonly label: string;
  readonly valueIso: string;
  readonly onChangeIso: (iso: string) => void;
  readonly required?: boolean;
}) {
  const parts: JalaliDateTimeParts = useMemo(() => {
    return (
      isoToJalaliParts(props.valueIso) ?? {
        ...nowJalaliParts(),
        hour: 10,
        minute: 0,
      }
    );
  }, [props.valueIso]);

  const years = useMemo(() => {
    const y = nowJalaliParts().jy;
    return range(y - 1, y + 2);
  }, []);

  const daysInMonth = jalaliMonthLength(parts.jy, parts.jm);
  const minuteOptions = useMemo(() => {
    const base = [0, 15, 30, 45];
    if (base.includes(parts.minute)) return base;
    return [...base, parts.minute].sort((a, b) => a - b);
  }, [parts.minute]);

  const patch = (next: Partial<JalaliDateTimeParts>) => {
    let jm = next.jm ?? parts.jm;
    let jd = next.jd ?? parts.jd;
    const jy = next.jy ?? parts.jy;
    const max = jalaliMonthLength(jy, jm);
    if (jd > max) jd = max;
    props.onChangeIso(
      jalaliPartsToIso({
        jy,
        jm,
        jd,
        hour: next.hour ?? parts.hour,
        minute: next.minute ?? parts.minute,
      }),
    );
  };

  return (
    <fieldset className="jalali-field">
      <legend>{props.label}</legend>
      <div className="jalali-grid">
        <label>
          روز
          <select
            value={parts.jd}
            required={props.required}
            onChange={(e) => patch({ jd: Number(e.target.value) })}
          >
            {range(1, daysInMonth).map((d) => (
              <option key={d} value={d}>
                {toFaDigits(d)}
              </option>
            ))}
          </select>
        </label>
        <label>
          ماه
          <select
            value={parts.jm}
            required={props.required}
            onChange={(e) => patch({ jm: Number(e.target.value) })}
          >
            {JALALI_MONTHS_FA.map((name, i) => (
              <option key={name} value={i + 1}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label>
          سال
          <select
            value={parts.jy}
            required={props.required}
            onChange={(e) => patch({ jy: Number(e.target.value) })}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {toFaDigits(y)}
              </option>
            ))}
          </select>
        </label>
        <label>
          ساعت
          <select
            value={parts.hour}
            required={props.required}
            onChange={(e) => patch({ hour: Number(e.target.value) })}
          >
            {range(0, 23).map((h) => (
              <option key={h} value={h}>
                {toFaDigits(String(h).padStart(2, "0"))}
              </option>
            ))}
          </select>
        </label>
        <label>
          دقیقه
          <select
            value={parts.minute}
            required={props.required}
            onChange={(e) => patch({ minute: Number(e.target.value) })}
          >
            {minuteOptions.map((m) => (
              <option key={m} value={m}>
                {toFaDigits(String(m).padStart(2, "0"))}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="jalali-hint">
        انتخاب شما:{" "}
        <strong>
          {toFaDigits(
            `${parts.jy}/${String(parts.jm).padStart(2, "0")}/${String(parts.jd).padStart(2, "0")} · ${String(parts.hour).padStart(2, "0")}:${String(parts.minute).padStart(2, "0")}`,
          )}
        </strong>
      </p>
    </fieldset>
  );
}
